import { useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AppStateContext, AppStateValue } from '../../AppState';
import { Lifecycle } from '../../lifecycle/utils/typings';
import { getCachedFeature } from '../../shared/cache/FeatureCache';
import { getCachedUsers } from '../../shared/cache/PlatformUserCache';
import { getCachedSlaDefinitions } from '../../shared/cache/SlaDefinitionCache';
import { ICustomProperty } from '../../shared/interfaces/customproperties.interface';
import { ISupportGroup } from '../../shared/interfaces/supportGroup.interface';
import { TicketProps } from '../../shared/interfaces/ticketproperties.interface';
import { platformService } from '../../shared/services/platform.service';
import { CustomPropertiesAnswerJsonKey, FILTERS_STRINGS, LICENSE_FEATURE, STATUS_GUID, TICKET_LIST_COLUMN_IDS } from '../../shared/utils/constants';
import { chunk } from '../../shared/utils/helper';
import { ticketSearchFilter } from '../../shared/utils/ticketSearchFilter';
import { ListInputItemsEnum } from '../ticketHelper';
import { CustomViewContext, DEFAULT_SORT_ORDER, EnumSortOrderType } from '../toolbar/CustomViewContextProvider';
import { checkIfDefaultFilter, getChildFiltersFromStorage, getSortOrderFromStorage, getViewIdFromStorage } from '../toolbar/CustomViewPopup/heplers/customPopupHelper';
import { ListContext, ListContextType } from './ListContextProvider';
import { BuildFilterQuery, objectFromSelectedFilters } from './TicketQueryHelper';
import { TICKET_FILTER_EXPANSIONS } from './filterExpansions';

export const DEFAULT_SORT_ORDER_FOR_QUERYING: ISortOrder = DEFAULT_SORT_ORDER || {
  orderBy: 1,
  orderType: EnumSortOrderType.desc
}

const api = new platformService();

// Caps the TicketId IN-list size per TicketSla request regardless of configured page size (25/50/100)
const SLA_ID_BATCH_SIZE = 25;

const getFirstDayOfWeek = () => {
  const today = new Date();
  return new Date(
    today.getUTCFullYear(),
    today.getMonth(),
    today.getDate() - today.getDay()
  );
};

const getFirstDayNextWeek = () => {
  const today = new Date();
  const daysToAdd = 7 - today.getDay();
  return new Date(
    today.getUTCFullYear(),
    today.getMonth(),
    today.getDate() + daysToAdd
  );
};

const getLastDayNextWeek = () => {
  const today = new Date();
  const daysToAdd = 14 - today.getDay();
  return new Date(
    today.getUTCFullYear(),
    today.getMonth(),
    today.getDate() + daysToAdd
  );
};

export const isUndefinedOrEmpty = (query: string) => {
  return typeof query === 'undefined' || query === '';
};

export const filterTickets = (state: AppStateValue) => {

  const queryCheckLast = (query: string) => query === '' ? '' : ' )';
  const queryCheckStart = (query: string) => query === '' ? '' : ' and (';
  const dateQueryCheck = (query: string) => query === '' ? '' : ' or ';

  let datesFilterSelected = state.filterDropdown.selectedValues[0].values.filter((sv: FilterAccordionValues) => sv.selected);
  let statusFilterSelected = state.filterDropdown.selectedValues[1].values.filter((sv: FilterAccordionValues) => sv.selected);
  let priorityFilterSelected = state.filterDropdown.selectedValues[2].values.filter((sv: FilterAccordionValues) => sv.selected);
  let categoryFilterSelected = state.filterDropdown.selectedValues[3].values.filter((sv: FilterAccordionValues) => sv.selected);
  let assigneeFilterSelected = state.filterDropdown.selectedValues[4].values.filter((sv: FilterAccordionValues) => sv.selected);
  let groupsFilterSelected = state.filterDropdown.selectedValues[5].values.filter((sv: FilterAccordionValues) => sv.selected);
  let lifecycleFilterSelected = state.filterDropdown.selectedValues[9].values.filter((sv: FilterAccordionValues) => sv.selected);
  
  let status = state.ticketStatus;
  let priorities = state.priority;
  let categories = state.categories;
  let assignees = state.usersInRole;
  let groups = state.supportgroups;
  let lifecycles = state.lifecycles;
  let getCurrentDateTime = new Date();
  let today = new Date(
    getCurrentDateTime.getUTCFullYear(),
    getCurrentDateTime.getMonth(),
    getCurrentDateTime.getDate()
  );

  let tomorrow = new Date(
    getCurrentDateTime.getUTCFullYear(),
    getCurrentDateTime.getMonth(),
    getCurrentDateTime.getDate() + 1
  );

  let query = '';

  query += state.filterDropdown.queryValue !== ''
    ? (queryCheckStart(query)) + ticketSearchFilter(state.filterDropdown.queryValue, { matchId: true, comments: 'all' }) + (queryCheckLast(query))
    : '';

  let datesquery = '';
  datesFilterSelected.forEach((selected: FilterAccordionValues) => {
    switch (selected.key) {
      case 0:
        datesquery += (dateQueryCheck(datesquery)) + `DueDate lt ${today.toJSON()}`;
        break;
      case 1:
        datesquery += (dateQueryCheck(datesquery)) + `DueDate eq ${today.toJSON()}`;
        break;
      case 2:
        datesquery += (dateQueryCheck(datesquery)) + `DueDate eq ${tomorrow.toJSON()}`;
        break;
      case 3:
        datesquery += (dateQueryCheck(datesquery)) + `(DueDate gt ${getFirstDayOfWeek().toJSON()}) and (DueDate lt ${getFirstDayNextWeek().toJSON()})`;
        break;
      case 4:
        datesquery += (dateQueryCheck(datesquery)) + `(DueDate gt ${getFirstDayNextWeek().toJSON()}) and (DueDate lt ${getLastDayNextWeek().toJSON()})`;
        break;
      case 5:
        datesquery += (dateQueryCheck(datesquery)) + `DueDate gt ${today.toJSON()}`;
        break;
      case 6:
        datesquery += (dateQueryCheck(datesquery)) + 'DueDate eq null';
        break;
      default: break;
    }
  });
  query += datesquery !== '' ? (queryCheckStart(query)) + `${datesquery}` + (queryCheckLast(query)) : '';

  let statusquery = '';
  statusFilterSelected.forEach((selected: FilterAccordionValues) => {
    status.forEach((s: TicketProps) => {
      if (s.Id === selected.key) {
        statusquery += (statusquery === '' ? '' : ' or ') + `StatusId eq ${s.Id}`;
      }
    });
  });
  query += statusquery !== '' ? (queryCheckStart(query)) + `${statusquery}` + (queryCheckLast(query)) : '';

  let priorityquery = '';
  priorityFilterSelected.forEach((selected: FilterAccordionValues) => {
    priorities.forEach((priority: TicketProps) => {
      if (priority.Id === selected.key) {
        priorityquery += (priorityquery === '' ? '' : ' or ') + `PriorityId eq ${priority.Id}`;
      }
    });
  });
  query += priorityquery !== '' ? (queryCheckStart(query)) + `${priorityquery}` + (queryCheckLast(query)) : '';

  let categoryquery = '';
  categoryFilterSelected.forEach((selected: FilterAccordionValues) => {
    categories.forEach((category: TicketProps) => {
      if (category.Id === selected.key) {
        categoryquery += (categoryquery === '' ? '' : ' or ') + `CategoryId eq ${category.Id}`;
      }
    });
  });
  query += categoryquery !== '' ? (queryCheckStart(query)) + `${categoryquery}` + (queryCheckLast(query)) : '';

  let assigneequery = '';
  assigneeFilterSelected.forEach((selected: FilterAccordionValues) => {
    if (selected.key === 0) {
      assigneequery += (assigneequery === '' ? '' : ' or ') + `AssigneeId eq null`;
    }
    assignees.forEach((assignee: PlatformUser) => {
      if (assignee.Id === selected.key) {
        assigneequery += (assigneequery === '' ? '' : ' or ') + `AssigneeId eq ${assignee.Id}`;
      }
    });
  });
  query += assigneequery !== '' ? (queryCheckStart(query)) + `${assigneequery}` + (queryCheckLast(query)) : '';

  let groupsquery = '';
  groupsFilterSelected.forEach((selected: FilterAccordionValues) => {
    if (selected.key === 0) {
      groupsquery += (groupsquery === '' ? '' : ' or ') + `SupportGroupId eq null`;
    }
    groups.forEach((group: ISupportGroup) => {
      if (group.Id === selected.key) {
        groupsquery += (groupsquery === '' ? '' : ' or ') + `SupportGroupId eq ${group.Id}`;
      }
    });
  });

  query += groupsquery !== '' ? (queryCheckStart(query)) + `${groupsquery}` + (queryCheckLast(query)) : '';

  let lifecyclequery = '';
  lifecycleFilterSelected.forEach((selected: FilterAccordionValues) => {
    if (selected.key === 0) {
      lifecyclequery +=
        (lifecyclequery === '' ? '' : ' or ') + `LifecycleId eq null`;
    }
    lifecycles.forEach((s: Lifecycle) => {
      if (s.Id === selected.key) {
        lifecyclequery +=
          (lifecyclequery === '' ? '' : ' or ') + `LifecycleId eq ${s.Id}`;
      }
    });
  });
  query +=
  lifecyclequery !== ''
      ? (queryCheckStart(query)) +
        `${lifecyclequery}` +
        (queryCheckLast(query))
      : '';

  return query;
};

interface SearchParams {
  id: string;
}

const ListFunctions = () => {
  const state = useContext(AppStateContext);
  const { listStates, setListStates } = useContext(ListContext);
  const { customViewStates } = useContext(CustomViewContext);
  const location = useLocation();
  const stateRef = useRef(listStates);

  const searchParams = location.search.substring(1).split('&').reduce((acc, param) => {
    const currentParamArr = param.split('=');
    acc = { ...acc, [currentParamArr[0]]: currentParamArr[1] };
    return acc;
  }, {} as SearchParams);

  const customViewId = searchParams?.id || getViewIdFromStorage('custom'); // sometimes searchParams?.id will exist but its relevant custom view will not. i.e; wrong id
  const defaultViewId = checkIfDefaultFilter(state.listFilter) ? state.listFilter : getViewIdFromStorage('default') ?? ListInputItemsEnum.All_Active;
  const isDefaultViewId = !customViewId || customViewStates.selectedCustomView === undefined; // customViewStates.selectedCustomView === undefined means 'searchParams?.id' exists but no custom_view exists for that id. i.e; invalid id

  const moveToPage = (action: string) => {
    setListStates((prev) => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false }));

    if (action === 'next') {
      setListStates((prev) => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false, currentPage: prev.currentPage + 1 }));
      getpaginatedTickets({ page: listStates.currentPage + 1, count: listStates.countPerPage });
    } else if ( action === 'previous') {
      setListStates((prev) => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false, currentPage: prev.currentPage - 1 }));
      getpaginatedTickets({ page: listStates.currentPage - 1, count: listStates.countPerPage });
    } else {
      const thisPage = parseInt(action) || 1
      setListStates((prev) => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false, currentPage: thisPage }));
      getpaginatedTickets({ page: thisPage, count: listStates.countPerPage });
    }
  };
  const countPerPage = (count: number) => {
    localStorage.setItem('ticketListItemPerPage', count.toString());
    setListStates(prev => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false, countPerPage: count }));
    getpaginatedTickets({ page: listStates.currentPage, count: count });
  };

  const getStatusIdByGuid = (statusGuid: string) => {
    return state.ticketStatus.find((status: TicketProps) => status.Guid === statusGuid)?.Id;
  };

  const parentDefaultFilter: {
    0: () => FilterType;
    1: () => FilterType;
    2: () => FilterType;
    3: () => FilterType;
    4: () => FilterType;
  } = {
    0: () => ({ Closed: [false], Resolved: [false] }),
    1: () => ({ Closed: [false], 'AssigneeOrCollaborator': [state.currentUserId], Resolved: [false] }),
    2: () => ({ Closed: [false], 'AssigneeId': [null], Resolved: [false] }),
    3: () => ({ StatusId: [getStatusIdByGuid(STATUS_GUID.CLOSED), getStatusIdByGuid(STATUS_GUID.RESOLVED)] }),
    4: () => ({ StatusId: [getStatusIdByGuid(STATUS_GUID.DEFLECTED)] }),
  };


  // The raw OData predicates for the active view, without the `$filter=` / `cpfilter=` prefixes
  // or `$orderby`. Split out of getFilterQuery so the board can AND these into its per-lane
  // request and stay in lockstep with the list — see Board.tsx / boardWindowing.combineLaneFilter.
  const getFilterExpressions = async (customViewStates: CustomViewStatesType, FiltersDropdownValues?: FilterPropsItems[], defaultFilters?: FilterPropsItems[], includeCustomProperties?: boolean): Promise<{ main: string; custom: string }> => {
    let isDefaultView: boolean;
    const isCustomPropertiesFeatureEnabled = await getCachedFeature(LICENSE_FEATURE.CustomProperties);

    // this condition will run on first render or on refresh if id exists in search params
    if (customViewId && (FiltersDropdownValues || customViewStates?.selectedCustomView?.FiltersDropdownValues)) {
      isDefaultView = false; // view is not from default views
    } else {
      isDefaultView = true; // view is from default views
    }

    const expandedProperties = TICKET_FILTER_EXPANSIONS;
    const childFiltervalues = (await getChildFiltersFromStorage()) || defaultFilters || state.filterDropdown.selectedValues;
    const customFilters = objectFromSelectedFilters(childFiltervalues, true);
    const parentFilter = isDefaultView
      ? {
          customFilters: parentDefaultFilter[defaultViewId](),
          modifiedDateCustomView: null,
          createdDateCustomView: null,
          customValueCustomView: []
        }
      : objectFromSelectedFilters(
          FiltersDropdownValues ||
            customViewStates?.selectedCustomView?.FiltersDropdownValues,
          false // Means - not a child filter
        );

    const { data } = await api.me();
    let modifiedDateFromCustomView = parentFilter.modifiedDateCustomView ?? customFilters.modifiedDateCustomView;
    let createdDateFromCustomView = parentFilter.createdDateCustomView ?? customFilters.createdDateCustomView;
    let customValueFromCustomView = (Object.keys(parentFilter.customValueCustomView)?.length ?? 0) > 0 ? parentFilter.customValueCustomView : customFilters.customValueCustomView;

    const valuesCustomViews: ValuesCustomView = {
      ModifiedDate: modifiedDateFromCustomView,
      CreatedDate: createdDateFromCustomView,
      CustomValues: customValueFromCustomView
    };

    const parentFilterQuery = BuildFilterQuery({
                                state,
                                currentUser: data,
                                filterObj: parentFilter.customFilters,
                                childFiltervalues,
                                expandedProperties,
                                valuesCustomViews,
                                includeCustomProperties:
                                  includeCustomProperties && isCustomPropertiesFeatureEnabled,
                              });
    const childFilterQuery = BuildFilterQuery({
                              state,
                              currentUser: data,
                              filterObj: customFilters.customFilters,
                              childFiltervalues,
                              expandedProperties,
                              valuesCustomViews,
                              includeCustomProperties:
                                includeCustomProperties && isCustomPropertiesFeatureEnabled,
                            });
    const _childFilterQuery = (parentFilterQuery.main && childFilterQuery.main) ? ` and ${childFilterQuery.main}` : childFilterQuery.main;
    const _childFilterCustom = (parentFilterQuery.custom && childFilterQuery.custom) ? ` and ${childFilterQuery.custom}` : childFilterQuery.custom;

    return {
      main: `${parentFilterQuery.main}${_childFilterQuery}`,
      custom: `${parentFilterQuery.custom}${_childFilterCustom}`,
    };
  };

  const getFilterQuery = async (customViewStates: CustomViewStatesType, FiltersDropdownValues?: FilterPropsItems[], defaultFilters?: FilterPropsItems[], sortOrderUponRefresh?: ISortOrder, includeCustomProperties?: boolean) => {
    const isCustomPropertiesFeatureEnabled = await getCachedFeature(LICENSE_FEATURE.CustomProperties);
    const { main, custom } = await getFilterExpressions(customViewStates, FiltersDropdownValues, defaultFilters, includeCustomProperties);

    const filterQuery = main ? `$filter=${main}` : '';
    const filterCustom = custom ? `cpfilter=${custom}` : '';

    const orderByStr = getOrderBy(sortOrderUponRefresh, includeCustomProperties && isCustomPropertiesFeatureEnabled);
    return [filterQuery, filterCustom, orderByStr].filter(x => (x?.length ?? 0) > 0).join('&');
  };

  const getOrderBy = (sortOrderUponRefresh?: ISortOrder, includeCustomProperties?: boolean) => {
    let orderBy = '';
    let sortOrder: ISortOrder =
    getSortOrderFromStorage() || 
    sortOrderUponRefresh ||
    customViewStates.configureColumnsApplied.sortOrder ||
    DEFAULT_SORT_ORDER_FOR_QUERYING;

    const orderByInt = sortOrder.orderBy;
    let orderByString = TICKET_LIST_COLUMN_IDS[orderByInt];
    if(!orderByString)
      orderByString = includeCustomProperties ? getCustomPropertyQueryValue(state?.customProperties, TICKET_LIST_COLUMN_IDS, orderByInt) : TICKET_LIST_COLUMN_IDS[1];
      
    const prefix = orderByString.startsWith(`${CustomPropertiesAnswerJsonKey}.`) ? 'cporderby=' : '$orderby=';

    orderBy = `${prefix}${orderByString} ${EnumSortOrderType[sortOrder.orderType]}`;
    return orderBy;
  }

  const _getpaginatedTicketsPromise = async ({
    page,
    count,
    customViewStates,
    FiltersDropdownValues,
    sortOrder: sortOrderUponRefresh,
    defaultFilters,
  }: {
    page: number;
    count: number;
    customViewStates: CustomViewStatesType;
    FiltersDropdownValues?: FilterPropsItems[]; // will exist, on hard refreshing or first time loading of tikets_list with custom view id either from search params or from local/session storage
    sortOrder?: ISortOrder; // will exist, on hard refreshing or first time loading of tikets_list
    defaultFilters?: FilterPropsItems[];
  }) => {

    let filterQuery = await getFilterQuery(customViewStates, FiltersDropdownValues, defaultFilters, sortOrderUponRefresh, true);
    const response = await api.getTicketList(`?$expand=Resolution,AffectedUsers($select=PlatformUserId),TicketCollaborators($select=PlatformUserId),TicketLifecycle($expand=Status,Lifecycle($select=Title),Phases($filter=IsCurrent eq true)),GroupChat($select=ConversationId,Id;$expand=Members($select=Id,AadObjectId,UserName,FullName))&${filterQuery}&$top=${count}&$count=true&$skip=${(page - 1) * count}`);
    
    const users = await loadUsers(response.data.value);
    response.data.value.forEach(t => {
      t.Requester = users.find(u => u.Id == t.RequesterId);
      t.Assignee = users.find(u => u.Id == t.AssigneeId);
      t.AffectedUsers?.forEach(a => a.PlatformUser = users.find(u => u.Id == a.PlatformUserId))
      t.TicketCollaborators?.forEach(a => a.PlatformUser = users.find(u => u.Id == a.PlatformUserId))
    });

    return response;
  };

  const getpaginatedTickets = async ({ page, count, FiltersDropdownValues, sortOrder, defaultFilters }: {
    page: number;
    count?: number;
    FiltersDropdownValues?: FilterPropsItems[];
    sortOrder?: ISortOrder;
    defaultFilters?: FilterPropsItems[];
  }) => {
    try {
      count ??= stateRef.current.countPerPage;
      const paginatedTickets = (
        await _getpaginatedTicketsPromise({
          page,
          count,
          customViewStates,
          FiltersDropdownValues,
          sortOrder,
          defaultFilters
        })
      )?.data;

      const tickets = paginatedTickets.value;
      
      attachSlas(tickets);
      attachData(tickets);

      setListStates((prev) => ({
        ...prev,
        isTicketsFetching: false,
        countTickets: paginatedTickets['@odata.count'],
        tickets: tickets, // data.value = tickets
      }));
    } catch (error) {
      if (defaultViewId != -100) {
        console.error('Error occurred in fetching tickets', error);
        setListStates(prev => ({ ...prev, isTicketsFetching: false, errorDuringFetch: true }));
      } else {
        // defaultViewId is briefly -100 during the TableMenu "force refresh" trick
        // (onClickDefaultView), which always throws here since there's no filter for
        // it. The real fetch that follows on the next tick supersedes this one, but
        // the flag still has to clear or the list gets stuck loading if that next
        // fetch is ever delayed or itself fails (e.g. a flaky mobile connection).
        setListStates(prev => ({ ...prev, isTicketsFetching: false }));
      }
    }
  };

  const attachData = async (data: any[], listContext?: ListContextType) => {
    try{
      if (listContext) {
        listContext.setListStates((prev) => ({ ...prev, tickets: data }));
      } else {
        setListStates(prev => ({ ...prev, tickets: data }));
      }
    } catch (e) {
      console.error(`Error attaching SLAs`, e);
    }
  }

  const loadUsers = async (tickets: any[]) => {
    const users = tickets
        .map(x => [x.RequesterId, x.AssigneeId, ...x.AffectedUsers.map(x => x.PlatformUserId), ...x.TicketCollaborators.map(x => x.PlatformUserId)])
        .flat()
        .filter(x => x != null && x != undefined);
      return await getCachedUsers(users);
  }
  const attachSlas = async (data: any[], listContext?: ListContextType) => {
    try {
      const ticketIds = data.map(x => x.Id);
      const slas = (await Promise.all(chunk(ticketIds, SLA_ID_BATCH_SIZE).map(ids =>
        api.getTicketSla(`${FILTERS_STRINGS.TICKET_SLA_FILTER_LEAN}&$filter=TicketId in (${ids.join(',')})`)
      ))).flat();

      const slaDefinitions = await getCachedSlaDefinitions(slas.map(s => s.ServiceLevelAgreementId));
      slas.forEach(s => { s.ServiceLevelAgreement = slaDefinitions.get(s.ServiceLevelAgreementId); });

      data.forEach(x => { x.TicketSlas = slas.filter(s => s.TicketId === x.Id); });

      if (listContext) {
        listContext.setListStates((prev) => ({
          ...prev,
          tickets: data
        }));
      } else {
        setListStates(prev => ({
          ...prev,
          tickets: data
        }));
      }
    } catch (e) {
      console.error(`Error attaching SLAs`, e);
    }
  };

  const getpaginatedTicketsOnEdit = async (
    page: number,
    count: number,
    listContext?: ListContextType,
    customViewStates?: CustomViewStatesType
  ) => {
    try {

      let paginatedTickets = (await _getpaginatedTicketsPromise({ page, count: count, customViewStates }))?.data;
      if (listContext && listContext.listStates.currentPage > 1 && paginatedTickets?.value?.length === 0) {
        const currentPage = listContext.listStates.currentPage;
        const count = listContext.listStates.countPerPage;
        // if we update a row which is only row in paginated_table(page) and current page is not first one, then currentPage will be decreased by one
        listContext.setListStates((prev) => ({ ...prev, currentPage: prev.currentPage - 1, }));
        paginatedTickets = (await _getpaginatedTicketsPromise({ page: currentPage - 1, count: count, customViewStates }))?.data;
      } 

      const tickets = paginatedTickets.value;
      
      attachSlas(tickets, listContext);

      listContext.setListStates((prev) => ({
        ...prev,
        isTicketsFetching: false,
        countTickets: paginatedTickets['@odata.count'],
        tickets: tickets, // data.value = tickets
      }));

      // clear selected rows
      listContext.listStates.rowsSelection?.setAllSelected(false);
    } catch (error) {
      console.error('Error occurred in fetching tickets');
    }
  };

  const setTicketInterval = (callback?: () => void) => setInterval(() => {
    if (callback) {
      callback();
    } else {
      const page = stateRef.current.currentPage;
      const count = stateRef.current.countPerPage;
      getpaginatedTickets({ page: page, count: count });
    }
  }, 300000);

  return {
    _getpaginatedTicketsPromise,
    getpaginatedTickets,
    getpaginatedTicketsOnEdit,
    moveToPage,
    countPerPage,
    searchParams,
    getStatusIdByGuid,
    customViewId,
    defaultViewId,
    isDefaultViewId,
    setTicketInterval,
    getFilterQuery,
    getFilterExpressions
  };
};

export default ListFunctions;

export const getCustomPropertyQueryValue = (customProperties: ICustomProperty[], keyValue: any, findId: number) => {
    const cp = customProperties.find((cp) => cp.Id == findId - 1000);
    if(cp)
      return `${CustomPropertiesAnswerJsonKey}.${cp.PropertyId}`;
    else
      return keyValue[1];
  }